import React from 'react';
import { CreditCard, Banknote, ArrowLeft, MapPin, Tag, X, Check, Gift, Store, Truck, Clock, Plus, ChevronRight, Trash2, Phone, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { formatPrice } from '../../lib/utils';
import StampCard from '../StampCard';

const ScheduleCard = ({
  availableDates,
  i18n,
  loading,
  loadingSlots,
  scheduledDate,
  scheduledTime,
  setScheduledDate,
  setScheduledTime,
  t,
  timeSlots,
}) => (
  <>
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-[#FFC107]" />
                    {t('schedule.title', 'Schedule Order')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Date Selection */}
                  <div className="space-y-2">
                    <Label>{t('schedule.selectDate', 'Select Date')}</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="schedule-dates">
                      {availableDates.map((d) => {
                        const dateObj = new Date(d.date + 'T00:00:00');
                        const isToday = d.date === new Date().toISOString().split('T')[0];
                        const dayLabel = isToday ? t('schedule.today', 'Today') : d.day;
                        const dateLabel = dateObj.toLocaleDateString(i18n.language === 'de' ? 'de-CH' : i18n.language === 'ar' ? 'ar' : i18n.language === 'it' ? 'it-CH' : 'en-GB', { day: 'numeric', month: 'short' });
                        return (
                          <button
                            key={d.date}
                            type="button"
                            onClick={() => setScheduledDate(d.date)}
                            className={`p-2 rounded-lg border text-center transition-colors ${
                              scheduledDate === d.date
                                ? 'border-[#FFC107] bg-[#FFC107]/20 text-[#FFC107]'
                                : 'border-zinc-700 hover:border-[#FFC107]/50 text-zinc-300'
                            }`}
                            data-testid={`schedule-date-${d.date}`}
                          >
                            <div className="text-xs font-medium">{dayLabel}</div>
                            <div className="text-sm font-bold">{dateLabel}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Slot Selection */}
                  <div className="space-y-2">
                    <Label>{t('schedule.selectTime', 'Select Time')}</Label>
                    {loadingSlots ? (
                      <p className="text-sm text-zinc-500 animate-pulse">{t('schedule.loading', 'Loading slots...')}</p>
                    ) : timeSlots.length > 0 ? (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="schedule-times">
                        {timeSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setScheduledTime(slot)}
                            className={`py-2 px-1 rounded-lg border text-center text-sm font-medium transition-colors ${
                              scheduledTime === slot
                                ? 'border-[#FFC107] bg-[#FFC107]/20 text-[#FFC107]'
                                : 'border-zinc-700 hover:border-[#FFC107]/50 text-zinc-300'
                            }`}
                            data-testid={`schedule-time-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-red-400" data-testid="no-slots-message">
                        {t('schedule.noSlots', 'No available slots for this date. Please select another date.')}
                      </p>
                    )}
                  </div>

                  {/* Selected schedule summary */}
                  {scheduledDate && scheduledTime && (
                    <div className="p-3 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30" data-testid="schedule-summary">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#FFC107] shrink-0" />
                        <span className="text-sm font-medium text-[#FFC107]">
                          {t('schedule.confirmed', 'Scheduled for')}: {new Date(scheduledDate + 'T00:00:00').toLocaleDateString(i18n.language === 'de' ? 'de-CH' : i18n.language === 'ar' ? 'ar' : i18n.language === 'it' ? 'it-CH' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} {t('schedule.at', 'at')} {scheduledTime}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
  </>
);

export default ScheduleCard;
